import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { trades } from "./data";
import { SectionHeading } from "./Engines";

const marketTrades = {
  gold: trades[0],
  crypto: trades[1],
  forex: trades[2],
};

function Chart({ seed }) {
  const candles = Array.from({ length: 40 }, (_, i) => {
    const base = 60 + Math.sin((i + seed) * 0.45) * 26 + Math.sin(i * 1.3) * 8;
    const body = Math.round(8 + Math.abs(Math.sin((i + seed) * 2.1)) * 22);
    return { i, y: Number(base.toFixed(2)), body, up: Math.sin((i + seed) * 1.1) > 0 };
  });
  return (
    <div className="relative h-72 w-full overflow-hidden rounded-xl border border-border/60 bg-surface/40 p-4">
      <div className="absolute inset-0 opacity-25 [background-image:linear-gradient(var(--border)_1px,transparent_1px),linear-gradient(90deg,var(--border)_1px,transparent_1px)] [background-size:48px_36px]" />
      <div className="relative flex h-full items-center gap-1.5">
        {candles.map((c) => (
          <motion.div
            key={c.i}
            initial={{ opacity: 0, scaleY: 0.4 }}
            whileInView={{ opacity: 1, scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ delay: c.i * 0.015 }}
            className="relative flex-1"
            style={{ height: "100%" }}
          >
            <motion.span
              animate={{ y: [0, -4, 2, 0] }}
              transition={{ duration: 5 + (c.i % 4), repeat: Infinity, ease: "easeInOut" }}
              className={`absolute left-1/2 w-full -translate-x-1/2 rounded-sm ${
                c.up ? "bg-bull" : "bg-bear"
              }`}
              style={{ bottom: `${c.y}%`, height: c.body }}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function TerminalSection() {
  const [market, setMarket] = useState("gold");
  const trade = marketTrades[market];

  return (
    <section id="terminal" className="relative py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Terminal"
          title="The desk view."
          sub="Chart, active positions and the AI brief in one uninterrupted surface."
        />

        <div className="glass grain rounded-3xl p-4 sm:p-6">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 sm:flex sm:justify-between">
            <Tabs value={market} onValueChange={(v) => setMarket(v)}>
              <TabsList className="bg-surface-2/70">
                <TabsTrigger value="gold">Gold</TabsTrigger>
                <TabsTrigger value="crypto">Crypto</TabsTrigger>
                <TabsTrigger value="forex">Forex</TabsTrigger>
              </TabsList>
            </Tabs>
            <span className="flex shrink-0 items-center gap-2 text-[10px] tracking-[0.2em] text-muted-foreground uppercase">
              <span className="live-dot" /> Streaming
            </span>
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <AnimatePresence mode="wait">
              <motion.div
                key={market}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
              >
                <Chart seed={market === "gold" ? 1 : market === "crypto" ? 7 : 13} />
              </motion.div>
            </AnimatePresence>

            <div className="space-y-4">
              <div className="rounded-xl border border-border/60 bg-surface/50 p-4">
                <p className="text-[11px] tracking-[0.2em] text-muted-foreground uppercase">
                  Active Trade
                </p>
                <p className="mt-2 font-mono text-xl">{trade.pair}</p>
                <p
                  className={`text-xs tracking-[0.2em] uppercase ${
                    trade.side === "LONG" ? "text-bull" : "text-bear"
                  }`}
                >
                  {trade.side} · {trade.status}
                </p>
              </div>

              <div className="rounded-xl border border-primary/25 bg-primary/5 p-4">
                <p className="text-[11px] tracking-[0.2em] text-primary uppercase">AI Brief</p>
                <dl className="mt-3 space-y-2 font-mono text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Entry</dt>
                    <dd>{trade.entry}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Invalidation</dt>
                    <dd className="text-bear">{trade.sl}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Targets</dt>
                    <dd className="text-bull">
                      {trade.t1} / {trade.t2}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Confidence</dt>
                    <dd>{trade.confidence}%</dd>
                  </div>
                </dl>

                <Dialog>
                  <DialogTrigger className="mt-4 w-full rounded-lg border border-primary/40 px-4 py-2 text-xs font-semibold tracking-[0.16em] text-primary uppercase transition-colors hover:bg-primary/10">
                    Why this trade?
                  </DialogTrigger>
                  <DialogContent className="glass border-primary/20">
                    <DialogHeader>
                      <DialogTitle className="tracking-tight">
                        {trade.pair} · AI Explanation
                      </DialogTitle>
                      <DialogDescription className="pt-2 text-sm leading-relaxed">
                        {trade.reason}
                      </DialogDescription>
                    </DialogHeader>
                    <p className="text-xs text-muted-foreground">
                      Risk Engine verdict: approved at {trade.confidence}% confidence within the
                      current exposure budget.
                    </p>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="rounded-xl border border-border/60 bg-surface/50 p-4">
                <p className="text-[11px] tracking-[0.2em] text-muted-foreground uppercase">
                  Risk Profile
                </p>
                <div className="mt-3 flex gap-2">
                  {['Low', 'Medium', 'High'].map((r) => (
                    <span
                      key={r}
                      className={`flex-1 rounded-lg border px-2 py-2 text-center text-xs ${
                        r === 'Medium'
                          ? 'border-primary/45 bg-primary/10 text-primary'
                          : 'border-border/60 text-muted-foreground'
                      }`}
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
